import React, { useState, useEffect } from 'react';
import { useI18n } from '../contexts/I18nContext';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { verifyResetToken, resetPassword } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle, Eye, EyeOff, Lock } from 'lucide-react';

export default function ResetPasswordPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await verifyResetToken(token);
      setTokenValid(true);
      setUserInfo(res.data);
    } catch (err) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('common.passwordsNoMatch'));
      return;
    }
    
    if (password.length < 6) {
      toast.error(t('common.passwordTooShort'));
      return;
    }
    
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      toast.success(t('common.passwordUpdated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verificando enlace...</p>
        </div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-card border border-border rounded-lg p-8 shadow-xl text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Enlace Inválido</h2>
          <p className="text-muted-foreground text-sm mb-6">
            El enlace ha expirado o no es válido. Por favor solicita uno nuevo.
          </p>
          <div className="space-y-3">
            <Link to="/forgot-password">
              <Button className="w-full">Solicitar nuevo enlace</Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="w-full">Volver al inicio</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-card border border-border rounded-lg p-8 shadow-xl text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">¡Contraseña Actualizada!</h2>
          <p className="text-muted-foreground text-sm mb-6">
            Tu contraseña ha sido actualizada correctamente. Ya puedes iniciar sesión.
          </p>
          <Button onClick={() => navigate('/login')} className="w-full" data-testid="go-to-login-btn">
            Iniciar Sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-lg p-8 shadow-xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="/logo-ofimex-white.png" 
              alt="OFIMEX" 
              className="h-12 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-foreground">
              Crear Nueva Contraseña
            </h1>
            {userInfo && (
              <p className="text-muted-foreground text-sm mt-2">
                Hola <span className="text-foreground font-medium">{userInfo.name}</span>, 
                crea tu nueva contraseña
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="password">Nueva Contraseña</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="pl-10 pr-10"
                  required
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="pl-10"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={loading || !password || !confirmPassword || password !== confirmPassword}
              data-testid="reset-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Actualizando...
                </>
              ) : (
                'Guardar Contraseña'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
